import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSSE } from "../context/SSEContext";
import { useUnityViewer } from "../context/UnityContext";
import { useVisualizer } from "../context/VisualizerContext";
import { useUnityOverlayBridge } from "./useUnityOverlayBridge";
import {
  clientToNorm as clientToNormUtil,
  focusUnity as focusUnityUtil,
  mapNormToHost as mapNormToHostUtil,
} from "../utils/overlayCoords";

const ImageOverlaySessionContext = createContext(null);

function isThisSession(msg, sessionId) {
  if (msg.session_id == null || !sessionId) return true;
  return String(msg.session_id) === String(sessionId);
}

export function useImageOverlaySession({ annotationTool = null } = {}) {
  const { registerCallback } = useSSE();
  const { iframeRef, sessionId } = useUnityViewer(false);
  const { sendUnityJrpc } = useVisualizer();

  const [poses, setPoses] = useState(() => new Map());
  const posesRef = useRef(poses);
  posesRef.current = poses;

  const annotationToolRef = useRef(annotationTool);
  annotationToolRef.current = annotationTool;

  const handlersRef = useRef({});

  const setHandlers = useCallback(partial => {
    Object.assign(handlersRef.current, partial);
  }, []);

  const clipHost =
    typeof document !== "undefined" ? iframeRef?.current?.parentElement : null;

  const focusUnity = useCallback(() => focusUnityUtil(iframeRef), [iframeRef]);

  const blurUnityForKeys = useCallback(() => {
    try {
      iframeRef?.current?.blur();
    } catch {
      /* ignore */
    }
  }, [iframeRef]);

  const mapNormToHost = useCallback(
    (normX, normY) => mapNormToHostUtil(iframeRef, normX, normY),
    [iframeRef]
  );

  const clientToNorm = useCallback(
    (clientX, clientY) => clientToNormUtil(iframeRef, clientX, clientY),
    [iframeRef]
  );

  const getHostAspect = useCallback(() => {
    const host = iframeRef?.current?.parentElement;
    const rect =
      host?.getBoundingClientRect() ??
      iframeRef?.current?.getBoundingClientRect();
    if (!rect || rect.height <= 0) return 1;
    return rect.width / rect.height;
  }, [iframeRef]);

  const applyPose = useCallback(
    (id, normX, normY, visible = true, percentX, percentY) => {
      const { left, top } = mapNormToHost(normX, normY);
      const prev = posesRef.current.get(id);
      const pos = { left, top, normX, normY, visible: visible !== false };
      if (percentX != null && percentY != null) { pos.percentX = percentX; pos.percentY = percentY; }
      else if (prev?.percentX != null && prev?.percentY != null) { pos.percentX = prev.percentX; pos.percentY = prev.percentY; }
      setPoses(prevMap => {
        const next = new Map(prevMap);
        next.set(id, pos);
        // Keep posesRef in sync for drag
        posesRef.current = next;
        return next;
      });
      return pos;
    },
    [mapNormToHost]
  );

  const deletePoses = useCallback(ids => {
    setPoses(prev => {
      const next = new Map(prev);
      for (const id of ids) next.delete(id);
      posesRef.current = next;
      return next;
    });
  }, []);

  const getPose = useCallback(id => posesRef.current.get(id), []);

  const setAnchor = useCallback(
    (id, normX, normY, percentX, percentY) => {
      const params = (percentX != null && percentY != null) ? { id, percentX, percentY } : { id, normX, normY };
      sendUnityJrpc("Image.overlay.anchor.set", params);
    },
    [sendUnityJrpc]
  );

  const clearAnchor = useCallback(
    id => {
      sendUnityJrpc("Image.overlay.anchor.clear", { id });
    },
    [sendUnityJrpc]
  );

  const suppressPan = useCallback(
    active => {
      sendUnityJrpc("Image.overlay.suppressPan", { active: !!active });
    },
    [sendUnityJrpc]
  );

  useEffect(() => {
    return () => {
      sendUnityJrpc("Image.overlay.suppressPan", { active: false });
    };
  }, [sendUnityJrpc]);

  useUnityOverlayBridge(iframeRef, {
    onPose: (id, normX, normY, visible, percentX, percentY) => {
      if (handlersRef.current.shouldIgnorePose?.(id)) return;
      applyPose(id, normX, normY, visible, percentX, percentY);
      handlersRef.current.onPose?.(id, normX, normY, visible);
    },
    onPointer: (phase, normX, normY) => {
      handlersRef.current.onPointer?.(phase, normX, normY);
    },
    onUnityMouse: (clientX, clientY) => {
      handlersRef.current.onUnityMouse?.(clientX, clientY);
    },
    onImageReady: () => {
      handlersRef.current.onImageReady?.();
    },
    onImageLoading: () => {
      handlersRef.current.onImageLoading?.();
    },
  });

  useEffect(() => {
    return registerCallback("zone_clicked", data => {
      const msg = data.message || {};
      if (!isThisSession(msg, sessionId)) return;
      const normX = Number(msg.normX);
      const normY = Number(msg.normY);
      if (!Number.isFinite(normX) || !Number.isFinite(normY)) return;

      if (annotationToolRef.current) {
        handlersRef.current.onZoneClickPlacement?.(normX, normY, msg);
        return;
      }
      handlersRef.current.onZoneClick?.(msg);
    });
  }, [registerCallback, sessionId]);

  useEffect(() => {
    return registerCallback("zone_hovered", data => {
      const msg = data.message || {};
      if (!isThisSession(msg, sessionId)) return;
      handlersRef.current.onZoneHover?.(msg);
    });
  }, [registerCallback, sessionId]);

  return useMemo(
    () => ({
      iframeRef,
      sessionId,
      clipHost,
      poses,
      posesRef,
      applyPose,
      deletePoses,
      getPose,
      setAnchor,
      clearAnchor,
      suppressPan,
      focusUnity,
      blurUnityForKeys,
      mapNormToHost,
      clientToNorm,
      getHostAspect,
      sendUnityJrpc,
      setHandlers,
      annotationTool,
    }),
    [
      iframeRef,
      sessionId,
      clipHost,
      poses,
      applyPose,
      deletePoses,
      getPose,
      setAnchor,
      clearAnchor,
      suppressPan,
      focusUnity,
      blurUnityForKeys,
      mapNormToHost,
      clientToNorm,
      getHostAspect,
      sendUnityJrpc,
      setHandlers,
      annotationTool,
    ]
  );
}

export function ImageOverlaySessionProvider({ annotationTool, children }) {
  const session = useImageOverlaySession({ annotationTool });
  return (
    <ImageOverlaySessionContext.Provider value={session}>
      {children}
    </ImageOverlaySessionContext.Provider>
  );
}

export function useImageOverlay() {
  const ctx = useContext(ImageOverlaySessionContext);
  if (!ctx) {
    throw new Error(
      "useImageOverlay must be used within ImageOverlaySessionProvider"
    );
  }
  return ctx;
}
